/**
 * useCapability — frontend capability check hook.
 *
 * Reads `claims?.role` from AuthContext, runs it through the shared
 * ROLE_CAPABILITIES matrix, returns boolean.
 *
 * NO automatic founder bypass. Per CLAUDE.md §7 + 2026-04-29 review,
 * founder is admin-portal-only. Any caller landing on `apps/client`
 * must pass capability checks via the matrix; founder lacks every
 * client-app cap, so this hook returns false for founder on every
 * client-app cap by design.
 *
 * Per-tenant kill-switch (OPEN-2026-05-05-D resolved 2026-05-05):
 *   When `companies/{companyId}.featureFlags.capabilityRbacEnabled === false`
 *   AND the caller passes `legacyAllow`, the hook falls back to
 *   `legacyAllow.includes(role)`. PR G cleanup deletes the parameter
 *   along with this branch. Default-true semantics: only strict `false`
 *   on the flag short-circuits.
 *
 * Companion: RequireCapability (route guard).
 */

import { useAuth } from './useAuth';
import { useCompany } from '../context/CompanyContext';
// Granular subpath imports — the `/auth` barrel re-exports `computeBrokerIds`
// which pulls in Node's `crypto` module, breaking webpack 5 client bundles.
// See packages/shared-utils/package.json exports + commit d95778a8 history.
import { hasCapability, hasAnyCapability, type Capability } from '@valutten/shared-utils/capabilities';
import type { AppRole } from '@valutten/shared-utils/roles';

/**
 * Returns true iff the current authenticated user's role grants `cap`.
 * Single-cap form.
 *
 * @param legacyAllow Pre-migration role allow-list, applied only when the
 *   per-tenant kill-switch is active. Migrated call sites must pass this
 *   prop; PR G cleanup will delete the parameter alongside the fallback.
 */
export function useCapability(cap: Capability, legacyAllow?: readonly AppRole[]): boolean {
  const { claims } = useAuth();
  const { company } = useCompany();
  const role = (claims?.role ?? null) as AppRole | null;

  const rbacDisabled = company?.featureFlags?.capabilityRbacEnabled === false;
  if (rbacDisabled && legacyAllow) {
    return role !== null && legacyAllow.includes(role);
  }

  return hasCapability(role, cap);
}

/**
 * Returns true iff the current authenticated user's role grants ANY of `caps`.
 * OR-semantic for sites that originally had `enforceRole([admin, broker])`.
 * The cap is the perimeter; any inline self-scope check stays in place at
 * the call site.
 *
 * @param legacyAllow See `useCapability`.
 */
export function useAnyCapability(
  caps: readonly Capability[],
  legacyAllow?: readonly AppRole[],
): boolean {
  const { claims } = useAuth();
  const { company } = useCompany();
  const role = (claims?.role ?? null) as AppRole | null;

  const rbacDisabled = company?.featureFlags?.capabilityRbacEnabled === false;
  if (rbacDisabled && legacyAllow) {
    return role !== null && legacyAllow.includes(role);
  }

  return hasAnyCapability(role, caps);
}
